import React, { useEffect, useState } from 'react';
import { Box, IconButton, TablePagination,Snackbar, Alert } from '@mui/material';
import * as XLSX from "xlsx";
import { getCustomerTransactions } from '../../../Services/authService';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../Context/ThemeContext';
import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';
import CustomerTableComponent from './CustomerTableComponent';
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import dayjs from 'dayjs';
import ExportDateRangeModal from '../../../Components/Common/ExportDateRangeModal';
import { formatDate, formatTime } from '../../../Components/Common/dateFormat';

export const formatDateOnly = (dateString) => formatDate(dateString);

export const formatTimeOnly = (dateString) => formatTime(dateString);

function TransactionSection({ membershipCardNumber }) {
    const { t } = useTranslation();
    const { mode } = useThemeContext();

    const btnColor = mode === 'dark' ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;
   
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);

    const handleOpenExportModal = () => setIsExportModalOpen(true);
    const handleCloseExportModal = () => setIsExportModalOpen(false);

    const columns = [
        { field: "dateOnly", label: t('membershipCards.Date') },
        { field: "timeOnly", label: t('vendingMachines.time') },
        // { field: "transaction_id", label: t('membershipCards.TransactionId') },
        { field: "location", label: t('membershipCards.Location') },
        { field: "payment_type", label: t('membershipCards.PaymentType') },
        { field: "amount", label: t('membershipCards.Amountplaceholder') },
        { field: "status", label: t("membershipCards.status") },
    ];

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const [exporting, setExporting] = useState(false);
const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'warning' });

const showSnackbar = (message, severity = 'warning') =>
    setSnackbar({ open: true, message, severity });

const handleCloseSnackbar = (_event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
};



    // const fetchTransactions = async () => {
    //     setLoading(true);
    //     try {
    //         const data = await getCustomerTransactions(membershipCardNumber, page, rowsPerPage);
    //         const transformedRows = (data.transactions || []).map(item => ({
    //             ...item,
    //             dateOnly: formatDateOnly(item.transaction_date),
    //             timeOnly: formatTimeOnly(item.transaction_date),
    //         }));
    //         setRows(transformedRows || []);
    //         setTotalRows(data.totalItems || 0);
    //     } catch (error) {
    //         console.error(t("membershipCards.errorFetchingTransactions"), error);
    //         setRows([]);
    //         setTotalRows(0);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const data = (await getCustomerTransactions(membershipCardNumber, page, rowsPerPage)) || {};

            const transactions = Array.isArray(data.transactions) ? data.transactions : [];

            const transformedRows = transactions.map(item => ({
                ...item,
                dateOnly: formatDateOnly(item.date),
                timeOnly: formatTimeOnly(item.date),
                payment_type: item.payment_method === 'Cashback' ? 'Bowl Return' : item.payment_type,
            }));

            setRows(transformedRows);
            setTotalRows(data.totalItems || 0);
        } catch (error) {
            console.error(t("membershipCards.errorFetchingTransactions"), error);
            setRows([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (membershipCardNumber) {
            fetchTransactions();
        }
    }, [membershipCardNumber, page, rowsPerPage]);

    // Fetch all transactions for export (handles pagination automatically, optional date range)
    const fetchAllTransactionsForExport = async (fromDate = null, toDate = null) => {
        const allRows = [];
        let currentPage = 0;
        let hasMore = true;

        while (hasMore) {
            const data = (await getCustomerTransactions(membershipCardNumber, currentPage, 100, fromDate, toDate)) || {};
            const transactions = Array.isArray(data.transactions) ? data.transactions : [];

            const transformedRows = transactions.map(item => ({
                ...item,
                dateOnly: formatDateOnly(item.date),
                timeOnly: formatTimeOnly(item.date),
                payment_type: item.payment_method === 'Cashback' ? 'Bowl Return' : item.payment_type,
            }));

            allRows.push(...transformedRows);

            hasMore = allRows.length < (data.totalItems || 0);
            currentPage++;
        }

        return allRows;
    };

    // Handle Export to Excel with optional date range
    // Handle Export to Excel with optional date range
const handleExport = async (dateRange) => {
    try {
        setExporting(true);
        setIsExportModalOpen(false);

        let fromDate = null;
        let toDate = null;

        if (dateRange[0]?.startDate && dateRange[0]?.endDate) {
            fromDate = dayjs(dateRange[0].startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
            toDate = dayjs(dateRange[0].endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss');
        }

        const allData = await fetchAllTransactionsForExport(fromDate, toDate);

        // Client-side date filter as safety net
        const filteredData = fromDate && toDate
            ? allData.filter(row => {
                const rowDate = dayjs(row.date);
                return rowDate.isAfter(dayjs(fromDate).subtract(1, 'second')) &&
                       rowDate.isBefore(dayjs(toDate).add(1, 'second'));
            })
            : allData;

        // No data: show snackbar instead of alert
        if (!filteredData || filteredData.length === 0) {
            showSnackbar(t("membershipCards.noDataToExport"), 'warning');
            return;
        }

        // Translate status values the same way the on-screen table does
        const translatedStatus = (status) => {
            if (status === 'Completed') return t('membershipCards.statusCompleted', 'Completed');
            if (status === 'Failed') return t('membershipCards.statusFailed', 'Failed');
            return status;
        };

        const exportData = filteredData.map(row => ({
            [t('membershipCards.Date')]: formatDateOnly(row.date),
            [t('vendingMachines.time')]: formatTimeOnly(row.date),
            // "Transaction ID": row.transaction_id,
            [t('membershipCards.Location')]: row.location || "-",
            [t('membershipCards.PaymentType')]: row.payment_type,
            [t('membershipCards.Amountplaceholder')]: row.payment_type === 'Debit'
                ? `€ ${row.amount != null ? Number(row.amount).toFixed(2) : row.amount}`
                : `+ € ${row.amount != null ? Number(row.amount).toFixed(2) : row.amount}`,
            [t('membershipCards.status')]: translatedStatus(row.status)
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
      const timestamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);

const fileName = t(
    "membershipCards.transactions_export_file_name",
    "Transactions"
);

XLSX.writeFile(workbook, `${fileName}_${timestamp}.xlsx`);
    } catch (error) {
        console.error("Export failed", error);
        showSnackbar(t("vendingMachines.errorExportingData", "Error exporting data"), 'error');
    } finally {
        setExporting(false);
    }
};
    return (
        <>
            <div>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", my: 1 }}>
                    <p>{t('membershipCards.TransactionsHistory')}</p>
                    <IconButton className='add-machine butt' onClick={handleOpenExportModal}
                        sx={{
                            borderRadius: '40px', padding: '12px 16px', backgroundColor: btnColor, color: "#000", mr: '7px', ml: '16px',
                            whiteSpace: 'nowrap',
                            "&:hover": {
                                backgroundColor: btnColor, // Same as default
                                color: "#000", // Same as default
                                boxShadow: "none", // Prevent shadow effects
                            },
                        }} >
                        {t('membershipCards.Export')} <FileUploadOutlinedIcon fontSize='small' sx={{
                            marginLeft: '10px', strokeWidth: '10', "@media (max-width: 426px)": {
                                fontSize: "13px"
                            }
                        }} />
                    </IconButton>
                </Box>
                {loading ? (
                    <p>{t("vendingMachines.Loading")}</p>
                ) : (
                    <>
                        <CustomerTableComponent
                            rows={rows || []}
                            columns={columns || []}
                        />
                        <TablePagination
                            component="div"
                            count={totalRows}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage={t('membershipCards.RowsPerPage', 'Rows Per Page')}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        />
                    </>
                )}
            </div>

           <ExportDateRangeModal
    open={isExportModalOpen}
    onClose={handleCloseExportModal}
    onExport={(dateRange) => handleExport(dateRange)}
/>

<Snackbar
    open={snackbar.open}
    autoHideDuration={4000}
    onClose={handleCloseSnackbar}
    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
        {snackbar.message}
    </Alert>
</Snackbar>
        </>
    )
}

export default TransactionSection;